/**
 * features/parent/ParentNoticesFeed.tsx
 * ------------------------------------------------------------------
 * Parent-facing list of broadcast notices. Presentational: it can own
 * its own listener (when used standalone) or share one passed down by
 * the parent portal via `controller` (so the portal can show an
 * unread badge without a second subscription).
 *
 * Opening a notice marks it read (writes reads/{uid}); the unread dot
 * clears immediately (optimistic).
 * ------------------------------------------------------------------
 */
import { useState } from 'react';
import { Megaphone, Mail, ChevronDown, ChevronRight, FileText, Image as ImageIcon, Download } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { SoftCard } from '@/features/finance/ui';
import { Spinner } from '@/components/ui/primitives';
import {
  useParentNotices,
  type UseParentNotices,
} from '@/features/communications/useParentNotices';
import { formatBytes, isImageAttachment } from '@/lib/parentNotices';

export default function ParentNoticesFeed({
  controller,
}: {
  controller?: UseParentNotices;
}) {
  const { userData } = useAuth();
  // Always call the hook (Rules of Hooks); pass undefined uid when a
  // shared controller is provided, so this instance is an inert no-op.
  const own = useParentNotices(
    controller ? undefined : userData?.uid,
    userData?.displayName,
  );
  const c = controller ?? own;

  const [openId, setOpenId] = useState<string | null>(null);

  function toggle(id: string) {
    setOpenId((cur) => {
      const next = cur === id ? null : id;
      if (next) void c.markAsRead(id);
      return next;
    });
  }

  if (c.loading) {
    return (
      <SoftCard>
        <div className="flex justify-center py-8">
          <Spinner label="Loading notices…" />
        </div>
      </SoftCard>
    );
  }

  if (c.notices.length === 0) {
    return (
      <SoftCard>
        <div className="flex flex-col items-center gap-2 py-8 text-center text-gray-500">
          <Megaphone size={28} className="text-gray-300" />
          <p className="text-sm">No notices yet. School announcements will appear here.</p>
        </div>
      </SoftCard>
    );
  }

  return (
    <div className="space-y-3">
      {c.notices.map((n) => {
        const read = c.isRead(n.id);
        const open = openId === n.id;
        return (
          <SoftCard key={n.id} hover={false} pad="p-0">
            <button
              type="button"
              onClick={() => toggle(n.id)}
              className="w-full flex items-start gap-3 p-4 text-left"
            >
              {/* unread dot */}
              <span className="pt-1.5 shrink-0">
                <span
                  className={`block w-2.5 h-2.5 rounded-full ${
                    read ? 'bg-transparent border border-gray-300' : 'bg-[#2d5295]'
                  }`}
                />
              </span>

              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span
                    className={`text-sm ${
                      read
                        ? 'font-medium text-gray-700 dark:text-gray-200'
                        : 'font-semibold text-gray-900 dark:text-white'
                    }`}
                  >
                    {n.title}
                  </span>
                  {n.priority === 'high' && (
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                      Important
                    </span>
                  )}
                  {n.emailOnPublish && (
                    <span title="Also sent by email" className="text-gray-400">
                      <Mail size={13} />
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-400 mt-0.5">
                  {n.createdByName}
                  {n.createdByName ? ' · ' : ''}
                  {new Date(n.createdAt).toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  })}
                </div>
                {open && (
                  <>
                    <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap mt-3">
                      {n.body}
                    </p>

                    {/* Attachments. An image is shown, not just listed — a
                        poster or a flyer is the message, and a parent should
                        not have to download it to find out what it says. A
                        PDF gets a row with its size, so nobody opens 8MB on
                        mobile data without knowing. */}
                    {!!n.attachments?.length && (
                      <div className="mt-3 space-y-2" onClick={(e) => e.stopPropagation()}>
                        {n.attachments.map((a) => (
                          <a
                            key={a.path || a.url}
                            href={a.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden hover:border-[#2d5295] transition-colors"
                          >
                            {isImageAttachment(a) && (
                              <img
                                src={a.url}
                                alt={a.name}
                                loading="lazy"
                                className="w-full max-h-72 object-contain bg-gray-50 dark:bg-gray-800"
                              />
                            )}
                            <div className="flex items-center gap-2.5 px-3 py-2 text-sm">
                              {isImageAttachment(a)
                                ? <ImageIcon size={15} className="text-[#2d5295] shrink-0" />
                                : <FileText size={15} className="text-[#2d5295] shrink-0" />}
                              <span className="truncate text-gray-800 dark:text-gray-100">{a.name}</span>
                              <span className="ml-auto text-xs text-gray-400 shrink-0">
                                {formatBytes(a.size)}
                              </span>
                              <Download size={14} className="text-gray-400 shrink-0" />
                            </div>
                          </a>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              <span className="pt-0.5 shrink-0 text-gray-400">
                {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </span>
            </button>
          </SoftCard>
        );
      })}
    </div>
  );
}
